import "@/App.css";

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";

import {
  AuthProvider,
  useAuth,
} from "@/context/AuthContext";

import { FarmProvider } from "@/context/FarmContext";
import { Layout } from "@/components/Layout";

import Login from "@/pages/Login";
import ComingSoon from "@/components/ComingSoon";

import { modules } from "@/modules/registry";


function Protected({ children }) {
  const { user } = useAuth();

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (user === false) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  return children;
}


function AppShell() {
  const { user } = useAuth();

  const isAdmin =
    user?.role === "admin";

  const isConsultant =
    user?.subscription_plan === "advisor" ||
    user?.subscription_plan === "consultant";


  return (
    <FarmProvider>

      <Layout>

        <Routes>

          {modules.map((module) => {
            const Component =
              module.component;

            const relativePath =
              module.path.replace(
                "/app",
                ""
              ) || "/";

            const routePath =
              relativePath === "/"
                ? ""
                : relativePath.slice(1);


            // ---------------------------------------------------
            // Admin-only routes
            // ---------------------------------------------------

            if (
              module.adminOnly &&
              !isAdmin
            ) {
              return (
                <Route
                  key={module.key}
                  path={routePath}
                  element={
                    <Navigate
                      to="/app"
                      replace
                    />
                  }
                />
              );
            }


            // ---------------------------------------------------
            // Consultant-only routes
            //
            // Allowed:
            // - Admin
            // - Consultant
            // - Advisor alias
            //
            // Blocked:
            // - Pro farmer
            // - inactive/non-consultant accounts
            // ---------------------------------------------------

            if (
              module.consultantOnly &&
              !isAdmin &&
              !isConsultant
            ) {
              return (
                <Route
                  key={module.key}
                  path={routePath}
                  element={
                    <Navigate
                      to="/app"
                      replace
                    />
                  }
                />
              );
            }


            // ---------------------------------------------------
            // Normal route
            // ---------------------------------------------------

            return (
              <Route
                key={module.key}
                path={routePath}
                element={
                  <Component />
                }
              />
            );
          })}


          {/* Legacy ration route */}

          <Route
            path="ration"
            element={
              <Navigate
                to="/app/nutrition-evaluation"
                replace
              />
            }
          />


          {/* Legacy nutrient requirements route */}

          <Route
            path="nutrient-requirements"
            element={
              <Navigate
                to="/app/nutrition-evaluation"
                replace
              />
            }
          />


          {/* Coming soon modules */}

          <Route
            path="soon/:key"
            element={
              <ComingSoon />
            }
          />


          {/* Unknown app routes */}

          <Route
            path="*"
            element={
              <Navigate
                to="/app"
                replace
              />
            }
          />

        </Routes>

      </Layout>

    </FarmProvider>
  );
}


function App() {
  return (
    <div className="App">

      <AuthProvider>

        <BrowserRouter>

          <Routes>

            {/* Public login */}

            <Route
              path="/login"
              element={
                <Login />
              }
            />


            {/* Protected application */}

            <Route
              path="/app/*"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            />


            {/* Catch all */}

            <Route
              path="*"
              element={
                <Navigate
                  to="/app"
                  replace
                />
              }
            />

          </Routes>

        </BrowserRouter>


        <Toaster
          position="bottom-right"
        />

      </AuthProvider>

    </div>
  );
}


export default App;